import { useCallback, useContext, useMemo } from 'react';
import { type ContentType, UploadContext, type UploadSuccessAction, type VideoType } from '../UploadContext';

export const useFileUpload = ({
  contentType,
  ref,
  videoType,
}: {
  contentType: ContentType;
  ref: string;
  videoType: VideoType;
}) => {
  const context = useContext(UploadContext);

  if (context === null) {
    throw new Error('useFileUpload must be used within an UploadProvider');
  }

  const { removeUpload, setUpload, uploads } = context;

  const startUpload = useCallback(
    ({ file, onSuccessAction }: { file: File; onSuccessAction: UploadSuccessAction }) => {
      setUpload({ contentType, file, onSuccessAction, ref, videoType });
    },
    [contentType, ref, videoType, setUpload],
  );

  const removeCurrentUpload = useCallback(() => {
    removeUpload({ contentType, ref, videoType });
  }, [removeUpload, contentType, ref, videoType]);

  return useMemo(
    () => ({
      file: uploads[contentType]?.[ref]?.[videoType],
      removeUpload: removeCurrentUpload,
      startUpload,
    }),
    [uploads, contentType, ref, videoType, startUpload, removeCurrentUpload],
  );
};
